import { orgSelectors } from 'ducks/orgs'
import useComponentsV2List, {
  joinCodesForComponentsV2Filter,
} from 'elements/hardwareSelectorV2/fetch/useComponentsV2List'
import { DistributorTradePricingContext } from 'elements/hardwareSelectorV2/HardwareSelectorCore'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { Box, ComponentVersionsInherit, ComponentVersions_3_0, Grid } from 'opensolar-ui'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useDistributorTradePricing from 'services/useDistributorTradePricing'
import { ActionType } from 'types/tracking'
import {
  BulkOptionType,
  DistributorDataTypeV2,
  HardwareComponentType,
} from '../../../elements/hardwareSelectorV2/types'
import { mapComponentTypesV2ToV1 } from '../../../util/misc'
import { useProductTileState } from '../catalogv3/ProductTile'
import DealBadge from '../deals/DealBadge'
import { isBundleDeal } from '../deals/dealEngine'
import { ProductDetailCheckoutMultiDistributor } from '../detail/content/ProductDetailCheckoutMultiDistributor'
import ProductDetailDescription3 from '../detail/content/ProductDetailDescription3'
import { ProductDetailNotFound } from '../detail/ProductDetail'
import MarketplaceDisclaimerBanner from '../elements/MarketplaceDisclaimerBanner'
import ProductGrid from '../home/ProductGrid'
import useEnabledHardwareSuppliers from '../hooks/useEnabledHardwareSuppliers'
import { useProductAffinity } from '../hooks/useProductAffinity'
import BundleDealSection from './BundleSection'
import ProductCard from './ProductCard3'
import TechnicalDetails3, { TechnicalDetailsLoading } from './TechnicalDetails3'

const ProductDetail3 = ({ componentCode, goBack }: { componentCode: string; goBack?(): void }) => {
  const decodedComponentCode = decodeURIComponent(componentCode)
  const codesFilter = useMemo(() => joinCodesForComponentsV2Filter([decodedComponentCode]), [decodedComponentCode])
  const { componentsData, loading: isFetching } = useComponentsV2List({
    filterValues: {
      codes: codesFilter,
      show_archived: 1,
    },
    perPage: 10,
    blockRequest: !decodedComponentCode,
  })
  const [distributor, setDistributor] = useState<DistributorDataTypeV2 | null>(null)
  const [bulkOption, setBulkOption] = useState<BulkOptionType | null>(null)
  const [shouldShowSelectedDistributorPricing, setShouldShowSelectedDistributorPricing] = useState(false)
  const component = useMemo(() => componentsData[0] as HardwareComponentType, [componentsData])
  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'product_detail_3',
    eventName: 'Hardware Product',
  })
  const {
    selectedDistributor,
    hasSelectedDistributor,
    onSetDistributor,
    showConnectDistributorLink,
    lowestPriceDistributor,
    displayDetailsDistributor,
    hasStocks,
    totalQuantityAvailable,
    currencySymbol,
    stockLevelLabel,
    isGlobalOutOfStock,
    showIsInStockElsewhere,
    discountPercentValue,
    dealPrice,
    pricePerWatt,
  } = useProductTileState({ component })

  const activeDistributor = distributor ?? displayDetailsDistributor
  const allDeals = activeDistributor?.deals ?? []

  const bundleDeal = allDeals.find((d) => isBundleDeal(d))

  const tradePrice = activeDistributor?.price || 0
  const tradeDiscountFraction = activeDistributor?.price_detail?.discount_value || 0

  const hasDealPriceChange = dealPrice < tradePrice

  const effectiveDiscountFraction = useMemo(() => {
    if (!hasDealPriceChange) return tradeDiscountFraction
    const originalPrice = tradeDiscountFraction > 0 ? tradePrice / (1 - tradeDiscountFraction) : tradePrice
    if (!originalPrice || originalPrice <= 0) return tradeDiscountFraction
    return (originalPrice - dealPrice) / originalPrice
  }, [tradePrice, dealPrice, tradeDiscountFraction, hasDealPriceChange])

  const { filteredSuppliers: hardwareDistributors } = useEnabledHardwareSuppliers()

  const orgId = useSelector(orgSelectors.getOrg)?.id || 0

  const { distributorTradePricing, isChecking } = useDistributorTradePricing({
    orgId,
    distributors: hardwareDistributors,
  })

  // Fetch product affinity recommendations
  const { data: affinityData, isLoading: affinityLoading } = useProductAffinity({
    orgId,
    productCode: decodedComponentCode,
    distributors: distributor ? [distributor.distributor] : undefined,
    inStock: true,
    limit: 10,
    timeframeDays: 180,
  })

  const onSetBulkOption = (bulkOption: BulkOptionType | null) => {
    setBulkOption(bulkOption)
  }

  const handleChangeDistributor = (distributor: DistributorDataTypeV2 | null, showShowDistributorPricing: boolean) => {
    trackUserInteraction(
      { type: ActionType.CLICK },
      {
        action: 'clicked',
        source: 'select_available_distributor',
        context: 'product_details',
        distributor: distributor?.distributor,
      }
    )
    setDistributor(distributor)
    setShouldShowSelectedDistributorPricing(showShowDistributorPricing)
  }

  if (!isFetching && (!componentsData || componentsData.length === 0)) {
    return <ProductDetailNotFound />
  }

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <DistributorTradePricingContext.Provider value={{ distributorTradePricing, isChecking }}>
        <Grid container spacing={3} style={{ padding: 20 }}>
          <Grid item container spacing={3}>
            <Grid item md={8} style={{ width: '100%' }}>
              <ProductCard
                hasSelectedDistributor={!!distributor}
                shouldShowSelectedDistributorPricing={shouldShowSelectedDistributorPricing}
                displayDetailsDistributor={displayDetailsDistributor}
                manufacturerUrl={component?.manufacturer_logo}
                manufacturerName={component?.manufacturer_name}
                outputPower={(component?.rating || 0) * 1000}
                currencySymbol={currencySymbol}
                loading={isFetching}
                bulkOption={bulkOption}
                dealPrice={hasDealPriceChange ? dealPrice : undefined}
                effectiveDiscountFraction={effectiveDiscountFraction}
                pricePerWatt={pricePerWatt}
                dealBadge={
                  <Box marginTop={1}>
                    <DealBadge
                      orderingV2={component?.ordering_v2 ?? []}
                      selectedDistributor={selectedDistributor?.distributor}
                      showTooltip
                    />
                  </Box>
                }
              />
            </Grid>
            <Grid item md={4} style={{ width: '100%' }}>
              <ProductDetailCheckoutMultiDistributor
                component={componentsData[0]}
                currencySymbol={currencySymbol}
                selectedDistributor={selectedDistributor}
                setSelectedDistributor={onSetDistributor}
                hasSelectedDistributor={hasSelectedDistributor}
                showConnectDistributorLink={showConnectDistributorLink}
                displayDetailsDistributor={displayDetailsDistributor}
                lowestPriceDistributor={lowestPriceDistributor}
                hasStocks={hasStocks}
                totalQuantityAvailable={totalQuantityAvailable}
                stockLevelLabel={stockLevelLabel}
                onChangeDistributor={handleChangeDistributor}
                bulkOption={bulkOption}
                setBulkOption={onSetBulkOption}
                isGlobalOutOfStock={isGlobalOutOfStock}
                discountPercentValue={discountPercentValue}
                showIsInStockElsewhere={showIsInStockElsewhere}
                dealPrice={hasDealPriceChange ? dealPrice : undefined}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <MarketplaceDisclaimerBanner />
          </Grid>
          {bundleDeal && activeDistributor && (
            <Grid item xs={12}>
              <BundleDealSection deal={bundleDeal} currentProductCode={decodedComponentCode} />
            </Grid>
          )}
          <Grid item xs={12}>
            {isFetching ? (
              <TechnicalDetailsLoading />
            ) : (
              <TechnicalDetails3
                productDetailData={component?.data}
                componentType={component && mapComponentTypesV2ToV1(component.type)}
                quantityPerItem={bulkOption?.quantity_per_item ?? distributor?.quantity_per_item ?? 0}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <ProductDetailDescription3 selectedDistributor={distributor || lowestPriceDistributor} />
          </Grid>

          {/* Product Affinity - "You May Also Like" Section */}
          {affinityData && affinityData.recommended_products.length > 0 && (
            <Grid item xs={12}>
              <ProductGrid
                carouselMode={true}
                componentsData={affinityData.recommended_products}
                title={'You May Also Like'}
                category="product_affinity"
                loading={affinityLoading}
                pageSize={10}
              />
            </Grid>
          )}
        </Grid>
      </DistributorTradePricingContext.Provider>
    </ComponentVersionsInherit>
  )
}

export default ProductDetail3
